import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { getEnv, getOrigin } from "@/constants/GlobalConstants";
import Home from "@/views/RgView.vue";
import RgMainRouterView from "@/router/RgMainRouterView.vue";
import RgLoginView from "@/views/RgLoginView.vue";
import RgSettingsView from "@/views/RgSettingsView.vue";
import { IPProjectLoaderViewRedesign, IPProjectLoaderViewRedesignAlfred } from "@xlabmedical/implant-planner";
import { IPProjectLoaderRg } from "@/views/IPProjectLoaderRg";
import { ServerAPI } from "@xlabmedical/medic-api";
import { hasAllowUnsupportedBrowser, isChromeUserAgent, isIOSDevice, MUnsupportedBrowser, REALGUIDE_WEB_BUILD_DATE } from "@xlabmedical/medic-web-framework";
import type { RouteLocationNormalized } from "vue-router";

export enum NamedRoutes {
    Root = "Root",
    Login = "Login",
    Patients = "Patients",
    Idi = "idi",
    Settings = "Settings",
    ImplantPlannerProjectLoaderView = "ImplantPlannerProjectLoaderView",
    ImplantPlannerProjectLoaderViewAlfred = "ImplantPlannerProjectLoaderViewAlfred",
    ExportToAlfred = "ExportToAlfredPatientList",
    OpenWeblinkOrder = "OpenWeblinkOrder",
    UnsupportedBrowser = "UnsupportedBrowser",
}

export const mainUserRoutes = [
    {
        path: "/patients/:id?",
        name: NamedRoutes.Patients,
        component: Home,
    },
    {
        path: "/idi/:id?",
        name: NamedRoutes.Idi,
        component: Home,
    },
    {
        path: "/settings",
        name: NamedRoutes.Settings,
        component: RgSettingsView,
    },

    {
        path: "/project/:projectHash/:fileHash",
        name: NamedRoutes.ImplantPlannerProjectLoaderView,
        component: IPProjectLoaderViewRedesign,
        props: (route: RouteLocationNormalized) => {
            const forceReadOnly = route.query.readOnly === "true" || isIOSDevice();
            return {
                loader: (api: ServerAPI) => new IPProjectLoaderRg(api),
                ...(forceReadOnly ? { readOnly: true } : {}),
            };
        },
    },
    {
        path: "/export/:projectHash/:alfredId",
        name: NamedRoutes.ImplantPlannerProjectLoaderViewAlfred,
        component: IPProjectLoaderViewRedesignAlfred,
        props: {
            loader: (api: ServerAPI) => new IPProjectLoaderRg(api),
        },
    },
    {
        path: "/alfred/import",
        name: NamedRoutes.ExportToAlfred,
        component: Home,
        props: {
            exportToAlfred: true,
        },
    },
    {
        path: "/weblink/:productionCenterName/patients/:projectName",
        name: NamedRoutes.OpenWeblinkOrder,
        beforeEnter: (to: RouteLocationNormalized) => {
            const projectName = String(to.params.projectName ?? "");
            const productionCenterName = String(to.params.productionCenterName ?? "");
            window.location.assign(`${getOrigin().replace("/planner", "")}/weblink/${productionCenterName}/patients/${encodeURIComponent(projectName)}`);
        },
    },
];

const routes: Array<RouteRecordRaw> = [
    {
        path: "/unsupported-browser",
        name: NamedRoutes.UnsupportedBrowser,
        component: MUnsupportedBrowser,
    },
    {
        path: "/",
        name: NamedRoutes.Root,
        component: RgMainRouterView,
        children: [...mainUserRoutes],
    },
    {
        path: "/login",
        name: NamedRoutes.Login,
        component: RgLoginView,
    },
];

const router = createRouter({
    history: createWebHistory(getEnv().BASE_URL),
    routes,
});

const projectLoaderRoutes = new Set<string>([NamedRoutes.ImplantPlannerProjectLoaderView, NamedRoutes.ImplantPlannerProjectLoaderViewAlfred]);

router.beforeEach((to) => {
    if (!projectLoaderRoutes.has(String(to.name))) {
        return true;
    }

    if (to.name === NamedRoutes.ImplantPlannerProjectLoaderView && isIOSDevice()) {
        return true;
    }

    if (hasAllowUnsupportedBrowser(to.query.allowUnsupportedBrowser)) {
        return true;
    }

    if (typeof navigator === "undefined" || isChromeUserAgent(navigator.userAgent)) {
        return true;
    }

    const targetUrl = typeof window === "undefined" ? to.fullPath : `${window.location.origin}${router.resolve(to).href}`;
    return {
        name: NamedRoutes.UnsupportedBrowser,
        query: { targetUrl },
    };
});

export const redirectToLogin = (isLogout?: boolean) => {
    if (window.location.host.startsWith("localhost:5") || window.location.search.includes("forceLocalLogin=true")) {
        return router.push({
            name: NamedRoutes.Login,
        });
    }
    const authURL = new URL("/auth/login", window.location.origin);
    let redirectTo = window.location.href.replace(window.location.origin, "");
    if (redirectTo.includes("login") || isLogout) redirectTo = "/planner";
    authURL.searchParams.set("redirectTo", redirectTo);
    authURL.searchParams.set("clientVersion", getEnv().VITE_BUILD_VERSION ?? "");
    authURL.searchParams.set("clientBuildDate", REALGUIDE_WEB_BUILD_DATE);
    window.location.href = authURL.toString();
};

export default router;
